import { startTransition, useEffect, useRef, useState } from "react";
import { BUILD_TIME } from "../constants/buildInfo";

/**
 * Polls /version.json every POLL_INTERVAL_MS milliseconds and sets
 * `isNewVersionAvailable = true` when the server's buildTime is newer than
 * the buildTime baked into the current JS bundle.
 *
 * The hook also stops polling once the user has seen the banner (they can
 * refresh whenever they're ready).
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useVersionCheck() {
	const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
	const intervalRef = useRef(null);
	const currentBuildTime = useRef(BUILD_TIME);

	useEffect(() => {
		async function checkVersion() {
			try {
				// Cache-bust so the browser never returns a stale response
				const res = await fetch(`/version.json?_=${Date.now()}`, {
					cache: "no-store",
				});
				if (!res.ok) return;
				const { buildTime } = await res.json();
				if (
					buildTime &&
					new Date(buildTime) > new Date(currentBuildTime.current)
				) {
					startTransition(() => setIsNewVersionAvailable(true));
					// Stop polling - no need to keep checking
					clearInterval(intervalRef.current);
				}
			} catch {
				// Network error or parse failure - silently ignore
			}
		}

		// Check immediately on mount (catches users who left the tab open
		// across a deployment without any page activity)
		checkVersion();
		intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

		return () => clearInterval(intervalRef.current);
	}, []);

	return { isNewVersionAvailable };
}
